import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from 'react-apollo-hooks';
import { loader } from 'graphql.macro';

import { withGraphQLErrors, normalizeFormData } from '@loyalup/core';

import { Hint, WithTitle, Checkbox, Button, Input, Form, useNotifications } from '@loyalup/ui';

import Phone from 'components/Phone';
import PhoneForm from 'components/PhoneForm';

import onlyData from 'utils/onlyData';
import estimateSmsForText from 'utils/sms';

import s from './DeliveryCardBySMS.module.css';

const COMPANY_QUERY = loader('../../gql/queries/Company.gql');
const UPDATE_CARD_DELIVERY_BY_SMS_SETTINGS_MUTATION = loader('../../gql/mutations/UpdateCardDeliveryBySmsSettings.gql');

const DeliveryCardBySMS = () => {
  const [t] = useTranslation();

  const [sendNotification] = useNotifications();

  const { data } = useQuery(COMPANY_QUERY, {
    suspend: false,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (data) {
      setForm(normalizeFormData(data.company?.cardDeliveryBySmsSettings));
    }
  }, [data]);

  const [updateSettings] = useMutation(UPDATE_CARD_DELIVERY_BY_SMS_SETTINGS_MUTATION);

  const onUpdateSettings = async () => {
    setLoading(true);
    setErrors({});
    const { errors } = await withGraphQLErrors(() =>
      updateSettings({
        variables: {
          cardDeliveryBySmsSettings: onlyData(form),
        },
        update: () => {
          sendNotification({ text: t('Сохранено') });
        },
      }),
    );

    setLoading(false);
    if (errors) {
      setErrors(errors);
    }
  };

  if (!form) {
    return null;
  }

  const smsLength = estimateSmsForText(form.message);

  return (
    <WithTitle title={t('Отправка карт по SMS')}>
      <div className={s.header}>
        <h1>{t('Отправка карт по SMS')}</h1>
        <div className={s.headerBlock}>
          <b>{t('Доступно SMS: {{count}}', { count: data?.company?.availableSms })}</b>
        </div>
      </div>
      <main className={s.main}>
        <PhoneForm
          renderPhone={() => (
            <Phone
              notificationTitle={'LoyalUP'}
              notificationText={form.message}
              notificationType={'SMS'}
              hint={t(
                'Триггерные уведомления срабатывают автоматически в зависимости от заданных условий, снимая с вас часть рутинных действий.',
              )}
            />
          )}
        >
          {() => (
            <Form className={s.settings} onSubmit={onUpdateSettings}>
              <label className={s.inputWithLabel}>
                <div className={s.columns}>
                  <span className={s.labelWithHint}>
                    {t('Текст сообщения')}
                    <Hint position="down" text={t('Ссылка на карту добавится к письму автоматически')} />
                  </span>
                  <span className={s.smsLength}>{t('SMS на сообщение: {{count, number}}', { count: smsLength })}</span>
                </div>
                <Input
                  required
                  type="textarea"
                  value={form.message}
                  error={errors.message}
                  placeholder={t('Введите текст сообщения')}
                  onChange={message => setForm({ ...form, message })}
                  className={s.input}
                  theme="toolbar-white"
                />
              </label>
              <Checkbox checked={form.auto} onChange={auto => setForm({ ...form, auto })} className={s.checkbox}>
                {t('Отправлять сообщение с картой автоматически, сразу после добавления клиента')}
              </Checkbox>
              <Button loading={loading} disabled={!form.message} fixedWidth={200} center type="submit">
                {t('Сохранить')}
              </Button>
            </Form>
          )}
        </PhoneForm>
      </main>
    </WithTitle>
  );
};

export default DeliveryCardBySMS;
